import { View, Text, Pressable, Image, TextInput, Switch, Button, Modal, FlatList, ScrollView, PanResponder } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import React, { FunctionComponent, useMemo, useState, useEffect, useRef } from 'react'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import Svg, { Path } from 'react-native-svg'
import dayjs from 'dayjs'



import { ProfilProps } from './Profil.types'
import { useProfilStyles } from './Profil.styles'
import { useProfilState } from './Profil.states'
import { ProfilActions } from './Profil.actions'
import { ProfilIBAN } from './ProfilIBAN'
import { ProfilSignature } from './ProfilSignature'

import { useSessionStore } from '../../lib/Store/sessionStore.component';

import { Password } from '../Password';
import { DocumentPicker } from '../../components/DocumentPicker';
import { Camera } from '../../components/Camera';

import { useNotify } from '../../components/Notification';

import { stdCouleurBleu, switchStyle, stdCouleurGris, stdCouleurBleuClair } from '@commonStyle';
import Base64Image from '../../components/Base64Image/Base64Image'
import { cameraImage } from '../../assets/images';
import { getBiometricLoginState, getBiometryLabel, disableBiometricLogin } from '../../lib/Biometrics';

// Affichage des blocs IBAN et Signature, désactivé temporairement
const AFFICHER_IBAN_SIGNATURE = false;

export const Profil: FunctionComponent<ProfilProps> = ({ navigation: _navigation }: any) => {
  const { getCurrentInfos } = useSessionStore();
  const { currentLogin, currentEntite, currentFiche } = getCurrentInfos();
  const notify = useNotify();
  const insets = useSafeAreaInsets();
  
  // Hook pour les styles dynamiques (réactif au redimensionnement)
  const { styles } = useProfilStyles();

  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [photoLoadError, setPhotoLoadError] = useState(false);
  const prevImageProfil = useRef<string | null | undefined>(undefined);

  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLabel, setBiometricLabel] = useState('la biométrie');
  const [biometricEnabled, setBiometricEnabled] = useState(false);

  useEffect(() => {
    const fetchBiometricState = async () => {
      const { availability, enabled } = await getBiometricLoginState();
      setBiometricAvailable(availability.available);
      setBiometricLabel(getBiometryLabel(availability.biometryType));
      setBiometricEnabled(enabled);
    };
    fetchBiometricState();
  }, []);

  const handleToggleBiometric = async (value: boolean) => {
    // On ne peut activer la connexion biométrique que depuis l'écran de connexion,
    // seul endroit où le mot de passe en clair est disponible pour l'enregistrer.
    if (value) return;
    await disableBiometricLogin();
    setBiometricEnabled(false);
    notify.success('Connexion biométrique désactivée');
  };

  // Réinitialiser l'erreur si l'image change (ex: upload d'une nouvelle photo)
  useEffect(() => {
    const current = currentFiche?.loginInfo.imageProfil;
    if (current !== prevImageProfil.current) {
      prevImageProfil.current = current;
      setPhotoLoadError(false);
    }
  }, [currentFiche?.loginInfo.imageProfil]);

  const entitesListe = useMemo(() => currentFiche?.collectivites?.map((entite) => ({
    value: String(entite.identifiantEntite),
    label: entite.libelle,
    logo: entite.logo,
  })) || [], [currentFiche]);

  const {
    dialogVisible,
    isModalVisible,
    email,
    // Setters
    setModalVisible,

    // Methods
    toggleSwitch,
    toggleDialog,
    changeMail,
    saveEmail,
    handleSelectEntite,

    // States IBAN / Signature
    signatureUri,
    setSignatureUri,
    ribUri,
    ribUploadDate,
    signatureUploadDate,
    setSignatureUploadDate,
    selectedRibDocument,
    selectedSignatureDocument,

    // Handlers IBAN / Signature
    handleRibFilePicked,
    handleRibRemove,
    handleRibDownload,
    handleSignatureFilePicked,
    handleSignatureRemove,
    handleSignatureDownload,
  } = useProfilState(notify);

  const [signatureModalVisible, setSignatureModalVisible] = useState(false);
  const [signatureText, setSignatureText] = useState<string | undefined>(currentFiche?.loginInfo.signature || undefined);

  // Modal pour dessiner la signature
  const [signatureDrawVisible, setSignatureDrawVisible] = useState(false);
  const [paths, setPaths] = useState<string[]>([]);
  const [currentPath, setCurrentPath] = useState<string>('');
  // Refs pour éviter les fermetures périmées dans les handlers du responder
  const currentPathRef = useRef<string>('');
  // Miroir synchrone de paths : garantit la valeur fraîche même dans les callbacks créés une seule fois
  const pathsRef = useRef<string[]>([]);
  // Ref sur le composant Svg pour capturer en PNG via toDataURL
  const svgDrawRef = useRef<any>(null);

  const signaturePanResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: (e) => {
        const { locationX, locationY } = e.nativeEvent;
        const d = `M ${locationX} ${locationY}`;
        currentPathRef.current = d;
        setCurrentPath(d);
      },
      onPanResponderMove: (e) => {
        const { locationX, locationY } = e.nativeEvent;
        currentPathRef.current = `${currentPathRef.current} L ${locationX} ${locationY}`;
        setCurrentPath(currentPathRef.current);
      },
      onPanResponderRelease: () => {
        const finishedPath = currentPathRef.current;
        if (finishedPath) {
          pathsRef.current = [...pathsRef.current, finishedPath];
          setPaths(pathsRef.current);
        }
        currentPathRef.current = '';
        setCurrentPath('');
      },
    })
  ).current;

  const renderPhoto = () => {
    const imageProfil = currentFiche?.loginInfo.imageProfil;
    const showImage = !photoLoadError && !!imageProfil;
    return (
    <View style={styles.photoContainer}>
      <Pressable
        onPress={toggleDialog}
        style={styles.photoPressable}
        testID='BT_PHOTO_PROFIL'
      >
        <View style={styles.photoWrapper}>
          <Image
            source={showImage ? { uri: imageProfil! } : cameraImage}
            style={styles.photoImage}
            onError={() => setPhotoLoadError(true)}
          />
        </View>
      </Pressable>
      <Text style={styles.userName}>{currentLogin?.nomClassement || 'Mon Profil'}</Text>
    </View>
  );
  };
  
  const renderMail = () => (  
    <View style={styles.itemContainer}>
      <View style={styles.itemHeader}>
        <View style={styles.iconContainer}>
          <MaterialCommunityIcons name="email" color={stdCouleurBleu} size={30} />
        </View>
        <Text style={styles.label}>Email</Text>
      </View>
      <View style={styles.itemContent}>
        <TextInput
          style={styles.emailInput}
          underlineColorAndroid="transparent"
          placeholderTextColor="grey"
          autoCapitalize="none"
          editable={true}
          value={email}
          onChangeText={changeMail}
          onEndEditing={saveEmail}
          onSubmitEditing={saveEmail}
          testID='SAI_MAIL'
        />
        <View style={styles.switchContainer}>
          <Switch
            trackColor={{ false: stdCouleurGris, true: stdCouleurBleuClair }}
            thumbColor={stdCouleurBleu}
            ios_backgroundColor={stdCouleurGris}
            onValueChange={toggleSwitch}
            value={!!currentFiche?.loginInfo.courrielEspace}
            style={switchStyle}
            testID='INT_MAIL'
          />
          <Text style={styles.switchText}>
            Recevoir des notifications par e-mail
          </Text>
        </View>
      </View>
    </View>
  );

  const renderMdp = () => (  
    <View style={styles.itemContainer}>
      <View style={styles.itemHeader}>
        <View style={styles.iconContainer}>
          <MaterialCommunityIcons name="lock" color={stdCouleurBleu} size={30} />
        </View>
        <Text style={styles.label}>Mot de passe</Text>
      </View>
      <View style={styles.buttonContainer}>
        <Button 
          title='Modifier mon mot de passe' 
          onPress={() => setShowPasswordReset(true)} 
          color={stdCouleurBleu} 
          testID='BT_PASSWORD'
        />
      </View>
    </View>
  );

  const renderBiometrie = () => {
    if (!biometricAvailable) return null;
    return (
      <View style={styles.itemContainer}>
        <View style={styles.itemHeader}>
          <View style={styles.iconContainer}>
            <MaterialCommunityIcons name="fingerprint" color={stdCouleurBleu} size={30} />
          </View>
          <Text style={styles.label}>Connexion biométrique</Text>
        </View>
        <View style={styles.itemContent}>
          <View style={styles.switchContainer}>
            <Switch
              trackColor={{ false: stdCouleurGris, true: stdCouleurBleuClair }}
              thumbColor={stdCouleurBleu}
              ios_backgroundColor={stdCouleurGris}
              onValueChange={handleToggleBiometric}
              value={biometricEnabled}
              style={switchStyle}
              testID='INT_BIOMETRIC'
            />
            <Text style={styles.switchText}>
              {biometricEnabled
                ? `Connexion avec ${biometricLabel} activée`
                : `Activez cette option depuis l'écran de connexion, avec votre identifiant et votre mot de passe`}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  //const openSignatureModal = () => setSignatureModalVisible(true);
  const openSignatureDraw = () => setSignatureDrawVisible(true);

  const saveSignatureText = async () => {
    try {
      await ProfilActions.updateSignatureText(signatureText || '', notify);
      setSignatureModalVisible(false);
    } catch (e) {
      // handled by action
    }
  };

  const renderEntite = () => (  
    <View style={styles.itemContainer}>
      <View style={styles.itemHeader}>
        <View style={styles.iconContainer}>
          <MaterialCommunityIcons name="office-building" color={stdCouleurBleu} size={30} />
        </View>
        <Text style={styles.label}>Entité</Text>
      </View>
      <View style={styles.entiteContainer}>
        <Pressable 
          style={styles.entitePressable}
          onPress={() => setModalVisible(true)}
          disabled={currentFiche?.collectivites?.length === 1}
          testID='BT_SELECTION_ENTITE'
        >
          <View style={styles.entiteRow}>
            <Text style={styles.entiteText}>
              {currentEntite?.libelle || "Choisissez une entité"}
            </Text>
            <>
            {currentFiche?.collectivites?.[0].logo && (
              <View style={styles.logoCenter}>
                <Base64Image base64={currentFiche?.collectivites?.[0].logo} width={100} height={50}/>
              </View>
              )
            }
           {currentFiche?.collectivites?.length !== 1 && (<MaterialCommunityIcons name="chevron-down" color="grey" size={25} />)}
           </>
          </View>
        </Pressable>
      </View>
    </View>
  );

  // Rendu du dialog de sélection photo
  const renderPhotoDialog = () => {
    const imageCallbacks = ProfilActions.createImagePickerCallbacks(toggleDialog);
    const photoCallbacks = ProfilActions.createPhotoPickerCallbacks(toggleDialog);
    
    return (
      <Modal visible={dialogVisible} animationType="fade" transparent>
        <View style={styles.dialogOverlay}>
          <View style={styles.dialogContainer}>
            <Text style={styles.dialogTitle}>Sélectionnez une option</Text>
            
            <DocumentPicker
              acceptedTypes={['image/*']}
              testID='BT_SELECTION_GALERIE'
              onFilePicked={imageCallbacks.onFilePicked}
              onError={imageCallbacks.onError}
              onCancel={imageCallbacks.onCancel}
            >
              <View style={styles.dialogOption}>
                <MaterialCommunityIcons name="folder-image" color={stdCouleurBleu} size={24} />
                <Text style={styles.dialogOptionText}>Galerie</Text>
              </View>
            </DocumentPicker>
            
            <Camera
              testID='BT_PRISE_PHOTO'
              onPhotoCaptured={photoCallbacks.onPhotoCaptured}
              onError={photoCallbacks.onError}
              onCancel={photoCallbacks.onCancel}
            >
              <View style={styles.dialogOption}>
                <MaterialCommunityIcons name="camera" color={stdCouleurBleu} size={24} />
                <Text style={styles.dialogOptionText}>Appareil photo</Text>
              </View>
            </Camera>
            
            <Pressable style={styles.dialogCancel} onPress={toggleDialog} testID='BT_EXIT_PHOTO'>
              <Text style={styles.dialogCancelText}>Annuler</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    );
  };

  // Rendu du modal de sélection d'entité
  const renderEntiteModal = () => {
    return (
    <Modal visible={isModalVisible} animationType="slide">
      <View style={[styles.modalContainer, { paddingTop: insets.top }]}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Sélectionner une entité</Text>
        </View>
        
        <FlatList
          data={entitesListe}
          keyExtractor={(item) => item.value}
          testID='SC_LISTE_ENTITES'
          renderItem={({ item, index }) => (
            <Pressable 
              style={styles.modalItem}
              onPress={() => handleSelectEntite(item)}
              testID={`BT_ENTITE${index + 1}`}
            >
              <Text style={styles.modalItemText}>{item.label}</Text>
              {item?.logo && (
                <View style={styles.logoCenterWithMargin}>
                  <Base64Image base64={item.logo} width={200} height={100}/>
                </View>
              )}
            </Pressable>
          )}
        />        
        <Pressable
          onPress={() => setModalVisible(false)}
          style={styles.modalCloseButton}
          testID='BT_EXIT_ENTITES'
        >
          <Text style={styles.modalCloseText}>Fermer</Text>
        </Pressable>
      </View>
    </Modal>
    )
  }

  return (
    <>
      {showPasswordReset ? (
        <Password onBack={() => setShowPasswordReset(false)} />
      ) : (
        <View style={styles.container}>
          <ScrollView contentContainerStyle={styles.scrollViewContent} testID='SC_MAIN_CONTENT'>
            <View style={[styles.innerContainer]}>
              {renderPhoto()}        
              <View style={styles.contentContainer}>
                {renderMail()}
                {renderMdp()}
                {renderBiometrie()}
                {renderEntite()}
                {AFFICHER_IBAN_SIGNATURE && (
                  <>
                    <ProfilIBAN
                      ribUri={ribUri}
                      ribUploadDate={ribUploadDate}
                      selectedRibDocument={selectedRibDocument}
                      onFilePicked={handleRibFilePicked}
                      onRemove={handleRibRemove}
                      onDownload={handleRibDownload}
                    />
                    <ProfilSignature
                      signatureUri={signatureUri}
                      signatureUploadDate={signatureUploadDate}
                      selectedSignatureDocument={selectedSignatureDocument}
                      onFilePicked={handleSignatureFilePicked}
                      onRemove={handleSignatureRemove}
                      onDownload={handleSignatureDownload}
                      onDraw={openSignatureDraw}
                    />
                  </>
                )}
                
              </View>
            </View>
          </ScrollView>
          
          {renderPhotoDialog()}
          {renderEntiteModal()}
          {(() => {
            return (
          <Modal visible={signatureModalVisible} animationType="slide">
            <View style={[styles.modalContainer, { paddingTop: insets.top }]}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Saisir la signature</Text>
              </View>
              <View style={{ padding: 16 }}>
                <TextInput
                  style={[styles.emailInput, { minHeight: 120, textAlignVertical: 'top' }]}
                  multiline
                  value={signatureText}
                  onChangeText={setSignatureText}
                  placeholder="Saisissez votre signature"
                />
                <View style={{ marginTop: 12 }}>
                  <Button title="Enregistrer" onPress={saveSignatureText} color={stdCouleurBleu} />
                </View>
                <View style={{ marginTop: 8 }}>
                  <Button title="Annuler" onPress={() => setSignatureModalVisible(false)} />
                </View>
              </View>
            </View>
          </Modal>
          )})()
          }

          {/* Modal de dessin de signature */}
          {(() => {
            return (
          <Modal visible={signatureDrawVisible} animationType="slide">
            <View style={[styles.modalContainer, { paddingTop: insets.top }]}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Dessiner la signature</Text>
              </View>
              <View style={{ padding: 16, alignItems: 'center' }}>
                <View style={styles.drawingArea} {...signaturePanResponder.panHandlers}>
                  <Svg ref={svgDrawRef} width={300} height={150} style={{ backgroundColor: 'white' }} pointerEvents="none">
                    {paths.map((p, idx) => (<Path key={idx} d={p} stroke="black" strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round" />))}
                    {currentPath ? (<Path d={currentPath} stroke="black" strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round" />) : null}
                  </Svg>
                </View>

                <View style={{ flexDirection: 'row', marginTop: 12 }}>
                  <Button title="Effacer" onPress={() => { setPaths([]); pathsRef.current = []; currentPathRef.current = ''; setCurrentPath(''); }} />
                  <View style={{ width: 12 }} />
                  <Button title="Annuler" color="grey" onPress={() => {
                    setSignatureDrawVisible(false);
                    setPaths([]);
                    pathsRef.current = [];
                    currentPathRef.current = '';
                    setCurrentPath('');
                  }} />
                  <View style={{ width: 12 }} />
                  <Button title="Enregistrer" color={stdCouleurBleu} onPress={async () => {
                    try {
                      const allPaths = [...pathsRef.current, currentPathRef.current].filter(Boolean);
                      if (allPaths.length === 0) {
                        notify?.error('Veuillez dessiner une signature avant d\'enregistrer');
                        return;
                      }
                      // Capturer la zone de dessin en PNG via toDataURL (react-native-svg v13+)
                      const pngBase64: string = await new Promise((resolve, reject) => {
                        if (!svgDrawRef.current?.toDataURL) {
                          reject(new Error('toDataURL non disponible'));
                          return;
                        }
                        svgDrawRef.current.toDataURL((data: string) => resolve(data));
                      });
                      const dataUri = `data:image/png;base64,${pngBase64}`;
                      const fileForUpload = { uri: dataUri, name: 'signature.png', type: 'image/png' } as any;
                      await ProfilActions.uploadSignatureImage(fileForUpload, notify);
                      setSignatureUri(dataUri);
                      setSignatureUploadDate(dayjs().format('DD/MM/YYYY'));
                      setSignatureDrawVisible(false);
                      setPaths([]);
                      pathsRef.current = [];
                      currentPathRef.current = '';
                      setCurrentPath('');
                    } catch (err) {
                      console.error('Erreur lors de l\'enregistrement du dessin :', err);
                      notify?.error('Erreur lors de l\'enregistrement de la signature');
                    }
                  }} />
                </View>
              </View>
            </View>
          </Modal>
          )})()}
        </View>
      )}
    </>
  );
}
